import {
    observer
} from 'mobx-react-lite';
import type { ApplicationSdk } from '../../stores';
import { useEffect, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Button } from 'primereact/button';
import ProductManagerStore from '../../stores/manager/ProductManagerStore';
import { formatDate } from '../../helper';
import { Toolbar } from 'primereact/toolbar';
import CategoryDetailStore from '../../stores/manager/common/CategoryEditStore';
import ProductDialogStore from '../../stores/manager/dialog/ProductDialogStore';
import SearchField from '../../components/SearchField';
import { Paginator } from 'primereact/paginator';
import { MultiSelect } from 'primereact/multiselect';
import { Chip } from 'primereact/chip';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';


const DeleteConfirmationDialog = observer(({ store, onConfirm, onCancel }: { store: any, onConfirm: () => void, onCancel: () => void }) => {
    const [confirmationText, setConfirmationText] = useState('');
    const searchWord: string = 'löschen';

    const handleConfirm = () => {
        if (confirmationText.toLowerCase() === searchWord) {
            setConfirmationText('');
            onConfirm();
        }
    };

    const handleCancel = () => {
        setConfirmationText('');
        onCancel();
    }

    const productName: string = store?.productDeleteConfirmation?.name || '';
    // const productId: number | null = store?.productDeleteConfirmation?.id || null;

    return (
        <Dialog
            visible={!!store.productDeleteConfirmation}
            modal
            header={'Produkt löschen bestätigen'}
            footer={() => (
                <div>
                    <Button
                        label="Abbrechen"
                        icon="pi pi-times"
                        className="p-button-secondary"
                        onClick={handleCancel}
                    />
                    <Button
                        label={'Löschen'}
                        icon="pi pi-check"
                        className="p-button-danger"
                        onClick={handleConfirm}
                        disabled={confirmationText.toLowerCase() !== searchWord}
                    />
                </div>
            )}
            onHide={handleCancel}
        >
            <p>Um das Produkt '{productName}' zu löschen, geben Sie bitte das Wort <strong>{'"löschen"'}</strong> in das Feld unten ein.</p>
            <InputText
                value={confirmationText}
                onChange={(e) => setConfirmationText(e.target.value)}
                placeholder={searchWord}
                style={{ width: '100%' }}
            />
        </Dialog>
    );
});

const FilterPopover = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const loadCategories = async () => {
        if (sdk.currentStore) {
            sdk.currentStore.fetchAvailableCategories();
        }
    };

    const onCategoryChange = (e: any) => {
        sdk.currentStore.addFilterCategory(e.value);
    };

    const onCategoryHide = () => {
        sdk.currentStore.applyFilterCategory();
    };

    return (
        <div>
            <h4 style={{ marginBottom: '1rem' }}>Kategorien wählen</h4>
            <MultiSelect
                value={sdk.currentStore?.filterCategories || []} // Selected categories
                options={sdk.currentStore?.available_categories || []} // Available categories
                onChange={onCategoryChange}
                onShow={loadCategories} // Lazy load categories when focused
                onHide={onCategoryHide} // Debug
                placeholder="Kategorien auswählen"
                optionLabel="name"
                selectionLimit={3} // Max labels to display
                display="chip" // Display selected categories as chips
                style={{ width: '100%' }}
            />
        </div>
    );
});

const FilterChips = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const selectedCategories = sdk.currentStore?.filterCategories || [];
    const selectedSearch = sdk.currentStore?.searchValue || '';
    return (
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {selectedCategories.map((category: any, i: number) => (
                <Chip
                    key={`chip-${category.id}`}
                    label={`Kategorie: ${category.name}`}
                    removable
                    onRemove={() => sdk.currentStore.removeFilterCategory(category)}
                />
            ))}
            {selectedSearch && (
                <Chip
                    label={`Suche: ${selectedSearch}`}
                    removable
                    onRemove={() => sdk.currentStore.clearSearch()}
                />
            )}
        </div>
    );
});
const CategoryManager = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    return <>
        <Button
            label="Neu"
            icon="pi pi-plus"
            aria-label="Filter"
            onClick={() => sdk.openDialog(ProductDialogStore, {})}
            style={{ marginRight: '0.5rem' }}
        />
        <Button
            label="Kategorien"
            icon="pi pi-cog"
            aria-label="Filter"
            onClick={() => sdk.openDialog(CategoryDetailStore, {})}
        />
    </>
});

const SearchToolbar = ({ sdk }: { sdk: ApplicationSdk }) => {
    const ref: any = useRef(null);
    return <>
        <Toolbar
            start={() => <CategoryManager sdk={sdk} />}
            center={() => (
                <>
                    <Button
                        icon="pi pi-filter"
                        className="p-button-text"
                        label="Filter"
                        onClick={(e: any) => ref.current?.toggle(e)}
                        aria-label="Filter"
                    />
                    <OverlayPanel ref={ref} style={{ width: '300px' }}>
                        <FilterPopover sdk={sdk} />
                    </OverlayPanel>
                </>
            )}
            end={() => <SearchField store={sdk.currentStore} placeholder={'Produkte durchsuchen'} />}
        />
        <FilterChips sdk={sdk} />
    </>
}


const ProductOverview = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(ProductManagerStore);
    }, [sdk]);

    const products = sdk.currentStore?.allProducts || null;

    const onPageChange = (event: any) => {
        sdk.currentStore.onPageChange(event.page + 1);
    };

    const rowClassName = (data: any) => {
        return {
            'red-row': !data.category?.name
        };
    };

    return <div className="card">
        <DataTable
            value={products}
            emptyMessage={'Keine Produkte gefunden'}
            header={<SearchToolbar sdk={sdk} />}
            onSort={(event: any) => {
                sdk.currentStore.onSort(event.sortField);
                return event;
            }}
            sortOrder={sdk.currentStore?.sortOrder || 1}
            sortField={sdk.currentStore?.sortField || 'id'}
            rowHover={true}
            loading={!!sdk.currentStore?.productListLoading}
            rowClassName={rowClassName}
        >
            {/* <Column field="pri" header="Status"></Column> PRICING? Free or paid*/}
            <Column field="image" header="" style={{ textAlign: 'center' }} body={(p: any) => (
                p.image ?
                    (<img src={p.image} alt="Product" style={{ width: '50px', height: '50px', borderRadius: '10%' }} />) :
                    (<i className="pi pi-image" style={{ fontSize: '2rem', textAlign: 'center' }} />)

            )} ></Column>
            <Column field="article_number" header="Artikel-Nummer" sortable ></Column>
            <Column
                field="name"
                header="Name"
                body={(product: any) => (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        {product.name}
                        {product.chargeable === 1 && (
                            <i
                                className="pi pi-euro"
                                style={{ fontSize: '0.8rem', color: '#E6007E', marginLeft: 8 }}
                                title="Kostenpflichtig"
                            ></i>
                        )}
                    </div>
                )}
            ></Column>
            <Column field="category.name" header="Kategorie"></Column>
            <Column field={'created'} header="Erstellt" sortable body={(p: any) => formatDate(p.created)}></Column>
            <Column field={'updated'} header="Aktualisiert" sortable body={(p: any) => formatDate(p.updated)}></Column>
            <Column
                header=""
                body={(p: any) => (
                    <>
                        <Button
                            icon="pi pi-pencil"
                            rounded
                            text
                            aria-label="Bearbeiten"
                            onClick={() => sdk.openDialog(ProductDialogStore, { product_id: p.id })}
                        />

                        {/* Bearbeiten-Button */}
                        {/* <Button
                            // label="Bearbeiten"
                            icon="pi pi-pencil"
                            iconPos="right" // Icon rechts neben dem Text
                            // className="p-button-outlined" // Umrandeter Button
                            rounded
                            // text
                            // aria-label="Bearbeiten"
                            onClick={() => sdk.openDialog(ProductDialogStore, { product_id: p.id })}
                        /> */}

                        {/* Löschbutton */}
                        <Button
                            icon="pi pi-trash"
                            className="p-button-text p-button-danger" // Weniger markant durch "p-button-text"
                            style={{ width: '2.5rem', height: '2.5rem' }} // Kleinere Größe
                            onClick={() => {
                                sdk.currentStore.onDeleteProduct(p); // Call the delete logic from the store
                                // if (confirm('Möchten Sie dieses Produkt wirklich entfernen?')) {
                                //     sdk.currentStore.removeProduct(p.id); // Call the remove logic from the store
                                // }
                            }}
                        />
                    </>
                )}
            ></Column>
        </DataTable>
        <Paginator
            first={((sdk.currentStore?.currentPage || 1) - 1) * 30}
            rows={30}
            totalRecords={sdk.currentStore?.totalCount || 1}
            onPageChange={onPageChange}
            // rowsPerPageOptions={30}
            template="PrevPageLink CurrentPageReport NextPageLink"
            currentPageReportTemplate={`Seite {currentPage} / {totalPages} (${sdk.currentStore?.totalCount || 0} Produkte)`}
        />
        <DeleteConfirmationDialog
            store={sdk.currentStore}
            onConfirm={() => sdk.currentStore.onDeleteProductConfirm()}
            onCancel={() => sdk.currentStore.onDeleteProductCancel()}
        />
    </div>;
});

export default ProductOverview;